/**
 * GbStripeService
 * Service to set and expose all of the necessary objects to operate stripe
 */
import { Inject, Injectable } from '@angular/core'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import {
  PaymentIntent,
  PaymentIntentResult,
  PaymentMethod,
  SetupIntent,
  SetupIntentResult,
  Stripe,
  StripeConstructorOptions,
  StripeElements,
  StripeExpressCheckoutElement,
  StripeExpressCheckoutElementConfirmEvent,
  StripePaymentElement,
  loadStripe,
} from '@stripe/stripe-js'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { GbAppPayment, GbPaymentIntent } from 'libs/gb-front/payment/util/src/lib/gb-payment.models'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import { BehaviorSubject, Observable, Subject, distinctUntilChanged, map, skip, tap } from 'rxjs'
import { GbStripeFacade } from './+state/gb-stripe.facade'
import * as StripeActions from './+state/gb-stripe.actions'

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class GbStripeService {
  private _stripe: Stripe | null = null
  private _elements?: StripeElements
  private _paymentElement?: StripePaymentElement
  private _expressCheckoutElement?: StripeExpressCheckoutElement
  private _setupIntent: SetupIntent | null = null
  private _payment?: GbAppPayment | GbPaymentIntent | null = null

  paymentElementSubject$ = new BehaviorSubject<StripePaymentElement | undefined>(undefined)
  paymentElementReadySubject$ = new BehaviorSubject<boolean>(false)
  paymentSubject$ = new Subject<GbAppPayment | GbPaymentIntent | null | undefined>()
  expressCheckoutElementSubject$ = new BehaviorSubject<StripeExpressCheckoutElement | undefined>(undefined)
  shouldShowDigitalWalletsSubject$ = new BehaviorSubject<boolean>(false)
  setupFutureUsageSubject$ = new BehaviorSubject<boolean>(this.appConfig.setupFutureUsage)

  /**
   * Stripe connect updated
   * Used to broadcast out to the app whenever the stripe connect account is updated.
   *
   * We are only subscribing to this observable in the components that need to:
   * gb-billing
   * gb-payment-dialog
   */

  stripeConnectUpdatedSubject$: Subject<string | undefined> = new Subject()
  stripeConnectUpdated$ = this.stripeConnectUpdatedSubject$.asObservable().pipe(
    tap((stripeConnect) => {
      const messageKey = stripeConnect
        ? 'ACCOUNT.BILLING.ADD_CARD.ADMIN_HAS_CHANGED_STRIPE'
        : 'ACCOUNT.BILLING.ADD_CARD.ADMIN_HAS_REMOVED_STRIPE'
      this.gbNotificationService.error(messageKey)
    }),
  )

  public set stripe(stripe) {
    this._stripe = stripe
  }

  public get stripe(): Stripe | null {
    return this._stripe
  }

  public set stripeElements(elements) {
    this._elements = elements
  }

  public get stripeElements(): StripeElements | undefined {
    return this._elements
  }

  public set paymentElement(paymentElement) {
    this._paymentElement = paymentElement
    this.paymentElementSubject$.next(paymentElement)
  }

  public get paymentElement(): StripePaymentElement | undefined {
    return this._paymentElement
  }

  public get paymentElement$(): Observable<StripePaymentElement | undefined> {
    return this.paymentElementSubject$.asObservable()
  }

  public set paymentElementReady(isPaymentElementReady: boolean) {
    this.paymentElementReady$.next(isPaymentElementReady)
  }

  public get paymentElementReady$() {
    return this.paymentElementReadySubject$
  }

  public set setupIntent(setupIntent: SetupIntent | null) {
    this._setupIntent = setupIntent
  }

  public get setupIntent(): SetupIntent | null {
    return this._setupIntent
  }

  public get paymentIntent(): PaymentIntent | undefined {
    if (!this._payment) return
    if ('stripe_payment_intent' in this._payment) {
      return this._payment.stripe_payment_intent?.payment_intent
    }
    return this._payment.payment_intent
  }

  public set payment(payment) {
    this._payment = payment
    this.paymentSubject$.next(payment)
  }

  public get payment() {
    return this._payment
  }

  public get payment$() {
    return this.paymentSubject$.asObservable()
  }

  public set expressCheckoutElement(expressCheckoutElement: StripeExpressCheckoutElement | undefined) {
    this._expressCheckoutElement = expressCheckoutElement
    this.expressCheckoutElementSubject$.next(expressCheckoutElement)
  }

  public get expressCheckoutElement(): StripeExpressCheckoutElement | undefined {
    return this._expressCheckoutElement
  }

  public get expressCheckoutElement$(): Observable<StripeExpressCheckoutElement | undefined> {
    return this.expressCheckoutElementSubject$.asObservable()
  }

  public set shouldShowDigitalWallets(shouldShow: boolean) {
    this.shouldShowDigitalWalletsSubject$.next(shouldShow)
  }

  public get shouldShowDigitalWallets$() {
    return this.shouldShowDigitalWalletsSubject$
  }

  public set setupFutureUsage(shouldSetUpFutureUsage: boolean) {
    this.setupFutureUsageSubject$.next(shouldSetUpFutureUsage)
  }

  public get setupFutureUsage$() {
    return this.setupFutureUsageSubject$
  }

  constructor(
    @Inject(APP_CONFIG) protected appConfig: any,
    private gbStripeFacade: GbStripeFacade,
    private auctionFacade: AuctionFacade,
    private loaderService: GbLoaderService,
    private gbNotificationService: GbNotificationService,
  ) {
    this.monitorStripeConnect().subscribe()
  }

  /**
   * Monitors stripe connect account
   * Dispatches all logic for when the stripe connect account changes
   *
   * Skip two is needed becuase the stripeConnect$ observable will first emmit undefined followed
   * by the stripe connect account and only the third will be a new stripe connect account.
   */

  monitorStripeConnect(): Observable<string | undefined> {
    return this.auctionFacade.stripeConnect$.pipe(
      map((stripeConnect) => stripeConnect?.uuid),
      untilDestroyed(this),
      distinctUntilChanged(),
      skip(2),
      tap((uuid) => {
        this.stripeConnectUpdatedSubject$.next(uuid)
      }),
    )
  }

  async initStripe(stripeUserId: string, isExpress: boolean): Promise<void> {
    const stripeOptions: StripeConstructorOptions = {}

    if (!isExpress && stripeUserId) {
      stripeOptions.stripeAccount = stripeUserId
    }

    return loadStripe(this.appConfig.stripe_publishable_key, stripeOptions).then((stripe) => {
      if (stripe && !this.stripe) this.stripe = stripe
    })
  }

  /**
   * Confirms card setup
   * @returns Promise resolving with a setupintent result object
   */
  async confirmCardSetup(): Promise<SetupIntentResult> {
    if (!this.stripe || !this.setupIntent || !this.setupIntent.client_secret || !this.stripeElements) {
      return Promise.reject()
    }

    await this.stripeElements.submit()

    return this.stripe.confirmSetup({
      elements: this.stripeElements,
      clientSecret: this.setupIntent.client_secret,
      redirect: 'if_required',
    })
  }

  async confirmPayment(
    paymentMethodId: string | null,
    ev?: StripeExpressCheckoutElementConfirmEvent,
    isPreAuth?: boolean,
  ): Promise<void> {
    this.loaderService.showLoaderDialog(LoaderActionTypeEnum.ConfirmCardPayment, 'PAYMENT.LOADER_CONFIRM_CARD_PAYMENT')

    const stripe = this.stripe
    const clientSecret = this.paymentIntent?.client_secret

    if (!clientSecret) {
      this.onPaymentError()
      return console.error('no client secret found')
    }
    if (!stripe) {
      this.onPaymentError()
      return console.error('no stripe found')
    }
    if (!this.stripeElements) {
      this.onPaymentError()
      return console.error('no stripe elements found')
    }

    let confirmPromise: Promise<PaymentIntentResult>

    if (paymentMethodId) {
      // saved card
      confirmPromise = stripe.confirmCardPayment(
        clientSecret,
        { payment_method: paymentMethodId },
        { handleActions: true },
      )
    } else {
      // new card or digital wallet
      if (!ev) await this.stripeElements.submit() // trigger form validation if card

      confirmPromise = stripe.confirmPayment({
        elements: this.stripeElements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.href },
      })
    }

    // Confirm the PaymentIntent handling potential next actions.
    return confirmPromise.then((confirmResult: PaymentIntentResult) => {
      if (confirmResult.error) {
        // Report to the browser that the payment failed, prompting it to
        // re-show the payment interface, or show an error message and close
        // the payment interface.
        return this.onPaymentError(confirmResult.error.message)
      } else {
        // Report to the browser that the confirmation was successful, prompting
        // it to close the browser payment method collection interface.

        // Check if the PaymentIntent requires any actions and if so let Stripe.js handle it
        if (confirmResult.paymentIntent.status === 'requires_action') {
          // Let Stripe.js handle the rest of the payment flow.
          if (stripe && clientSecret)
            return confirmPromise.then((result) => {
              if (result.error) {
                // The payment failed -- ask your customer for a new payment method.
                return this.onPaymentError(result.error.message)
              } else {
                // The payment has succeeded.
                return this.onPaymentSuccess(result.paymentIntent.payment_method, ev, isPreAuth)
              }
            })
        } else {
          // The payment has succeeded.
          return this.onPaymentSuccess(confirmResult.paymentIntent.payment_method, ev, isPreAuth)
        }
      }
    })
  }

  onPaymentSuccess(
    paymentMethod: string | PaymentMethod | null,
    ev?: StripeExpressCheckoutElementConfirmEvent,
    isPreAuth?: boolean,
  ): void {
    this.gbStripeFacade.dispatch(
      StripeActions.handlePaymentCompletion({
        paymentMethodId: paymentMethod as string,
        isDigitalWallet: !!ev, // ev is undefined if not digital wallet
        isPreAuth,
      }),
    )
  }

  onPaymentError(errorMesage = 'PAYMENT.ERROR'): void {
    this.gbNotificationService.error(errorMesage)
    this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.ConfirmCardPayment)
  }

  clearPayment(): void {
    this.payment = null
  }

  resetStripeElements(): void {
    this.paymentElement = undefined
    this.paymentElementReady = false
    this.expressCheckoutElement = undefined
    this.shouldShowDigitalWallets = false
  }
}
